import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Slider from '@material-ui/core/Slider';
import defer from 'lodash/defer';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import { KEY_CODES } from 'legacy-src/utility/utils';

const zoomOutIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width="32"
    height="32"
  >
    <g fill="none" fillRule="nonzero">
      <path fill="#FDFDFD" d="M0 0h32v32H0z " />
      <path
        fill="#000"
        d="M23.8 22.8l-4.1-4.2a6.5 6.5 0 1 0-1 1l4 4.2a.7.7 0 0 0 1 0c.4-.3.4-.8 0-1zm-9.3-3.2a5 5 0 1 1 0-10.1 5 5 0 0 1 0 10.1zm3-5c0 .3-.4.7-.8.7h-4.3a.7.7 0 0 1 0-1.5h4.3c.4 0 .8.3.8.7z"
      />
    </g>
  </svg>
);

const zoomInIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width="32"
    height="32"
  >
    <g fill="none" fillRule="nonzero">
      <path fill="#FDFDFD" d="M0 0h32v32H0z" />
      <path
        fill="#1E1E1E"
        d="M23.6 22.8l-4-4.2A6.6 6.6 0 0 0 14.4 8a6.5 6.5 0 1 0 0 13c1.5 0 2.9-.4 4-1.3l4 4a.7.7 0 0 0 1.1 0c.3-.2.3-.7 0-1zm-9.1-3.2a5 5 0 0 1-5-5 5 5 0 0 1 5-5.1 5 5 0 0 1 5 5 5 5 0 0 1-5 5.1zm2.8-5c0 .3-.3.7-.7.7h-1.4v1.4c0 .4-.3.8-.7.8a.7.7 0 0 1-.7-.8v-1.4h-1.5a.7.7 0 0 1-.7-.8c0-.4.3-.7.7-.7h1.5v-1.4c0-.4.3-.8.7-.8.4 0 .7.4.7.8v1.4h1.4c.4 0 .7.3.7.7z"
      />
    </g>
  </svg>
);

/**
 * Draws zoom slider container
 * */
class SliderContainer extends PureComponent {
  static displayName = 'SliderContainer';

  static propTypes = {
    onSliderChange: PropTypes.func,
    onZoom: PropTypes.func,
    isAvailable: PropTypes.bool,
    playerContainer: PropTypes.instanceOf(Element),
    onClose: PropTypes.func,
  };

  static defaultProps = {
    onSliderChange: () => {},
    onZoom: () => {},
    onClose: () => {},
    isAvailable: false,
    playerContainer: document,
  };

  constructor(props) {
    super(props);

    this.sliderRef = React.createRef();

    this.sliderStep = 10;
    this.minSliderValue = 100;
    this.maxSliderValue = 200;

    const zoomLevel = window.localStorage.getItem('zoom-level');
    this.state = {
      sliderValue: zoomLevel || 100,
      sliderKey: 0,
      focusedElement: null,
    };

    this.onZoomOutClick = this.onZoomOutClick.bind(this);
    this.onZoomInClick = this.onZoomInClick.bind(this);
    this.onSliderChange = this.onSliderChange.bind(this);
    this.applyZoom = this.applyZoom.bind(this);
    this.getZoomValue = this.getZoomValue.bind(this);
    this.close = this.close.bind(this);
  }

  componentDidMount() {
    this.zoomContainer = this.props.playerContainer.querySelector(
      '.lrn-assess .slides-control',
    );
    this.applyZoom(this.state.sliderValue);
    document.addEventListener('keydown', this.close);
  }

  componentWillUnmount() {
    this.applyZoom(100);
    window.localStorage.removeItem('zoom-level');
    document.removeEventListener('keydown', this.close);
  }

  /**
   * Zoom out handler
   * */
  onZoomOutClick(e) {
    e.stopPropagation();
    this.setState(({ sliderValue }) => {
      const newSliderValue = sliderValue - this.sliderStep;

      if (newSliderValue >= this.minSliderValue) {
        this.props.onSliderChange(newSliderValue);
        this.applyZoom(newSliderValue);
        window.localStorage.setItem('zoom-level', newSliderValue);
        return {
          sliderValue: newSliderValue,
        };
      }
    });
  }

  /**
   * Zoom in handler
   * */
  onZoomInClick(e) {
    e.stopPropagation();
    this.setState(({ sliderValue }) => {
      const newSliderValue =
        parseInt(sliderValue, 10) + parseInt(this.sliderStep, 10);

      if (newSliderValue <= this.maxSliderValue) {
        this.props.onSliderChange(newSliderValue);
        this.applyZoom(newSliderValue);
        window.localStorage.setItem('zoom-level', newSliderValue);
        return {
          sliderValue: newSliderValue,
        };
      }
    });
  }

  /**
   * Slider change event handler
   * @param {object} event
   * @param {*} value The slider value
   * */
  onSliderChange(event, value) {
    this.setState({
      sliderValue: value,
    });
    this.applyZoom(value);
    this.props.onSliderChange(value);
    window.localStorage.setItem('zoom-level', value);
    defer(() => {
      this.sliderRef.current.querySelector('[role="slider"]').focus();
    });
  }

  /**
   * Apply zoom to question content
   * @param {number} zoomValue
   * */
  applyZoom(zoomValue) {
    this.props.onZoom(zoomValue);

    this.zoomContainer.style.transform = `scale(${zoomValue / 100})`;
  }

  /**
   * Get zoom value
   * */
  getZoomValue() {
    return this.state.sliderValue;
  }

  close(e) {
    if (e.keyCode === KEY_CODES.esc) {
      this.props.onClose();
    }
  }

  render() {
    if (!this.props.isAvailable) {
      return null;
    }

    // A11y used here to provide a correct keycodes on keyDown for zoom buttons, so they would not fire when tab is pressed.
    const { sliderValue, sliderKey } = this.state;
    return (
      <div
        className="Zoom--slider-container"
        aria-live="polite"
        role="region"
        id="zoomSlider"
      >
        <A11y>
          <button
            className="Zoom--zoomout"
            onClick={this.onZoomOutClick}
            onKeyDown={this.onZoomOutClick}
            aria-label="Zoom out"
          >
            {zoomOutIcon}
          </button>
        </A11y>
        <div className="Zoom--slider">
          <Slider
            key={sliderKey}
            classes={{
              thumb: 'Slider--thumb',
            }}
            ref={this.sliderRef}
            value={Number(sliderValue)}
            onChange={this.onSliderChange}
            step={this.sliderStep}
            min={this.minSliderValue}
            max={this.maxSliderValue}
            aria-label="Zoom level"
            aria-valuetext={String(sliderValue)}
            onChangeCommitted={() => {
              this.setState({ sliderKey: sliderKey + 1 });
            }}
          />
        </div>
        <A11y>
          <button
            className="Zoom--zoomin"
            onClick={this.onZoomInClick}
            onKeyDown={this.onZoomInClick}
            aria-label="Zoom in"
          >
            {zoomInIcon}
          </button>
        </A11y>
        <div className="Zoom--zoomvalue">{sliderValue}%</div>
      </div>
    );
  }
}

export default SliderContainer;
